// SLA Management Types
// TypeScript definitions for SLA system components

export interface SLAPolicy {
  id: string;
  name: string;
  description?: string;
  firstResponseLowPriorityMinutes: number;
  firstResponseMediumPriorityMinutes: number;
  firstResponseHighPriorityMinutes: number;
  firstResponseUrgentPriorityMinutes: number;
  firstResponseCriticalPriorityMinutes: number;
  resolutionLowPriorityMinutes: number;
  resolutionMediumPriorityMinutes: number;
  resolutionHighPriorityMinutes: number;
  resolutionUrgentPriorityMinutes: number;
  resolutionCriticalPriorityMinutes: number;
  businessHoursStart: string; // TimeSpan as string "09:00:00"
  businessHoursEnd: string;   // TimeSpan as string "17:00:00"
  businessDays: string;       // Comma-separated: "Monday,Tuesday,Wednesday,Thursday,Friday"
  timeZone: string;
  excludeWeekends: boolean;
  excludeHolidays: boolean;
  businessHoursOnly: boolean;
  businessHoursCalendarId?: string;
  tier: SLATier;
  autoPauseOnCustomerWait: boolean;
  enablePredictiveSLA: boolean;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt?: string;
  createdById?: string;
  updatedById?: string;
}

export interface EscalationRule {
  id: string;
  name: string;
  description?: string;
  slaPolicyId: string;
  applicablePriorities: EscalationPriorityFlags;
  escalationLevel: number;
  triggerType: EscalationTriggerType;
  triggerMinutesBeforeBreach: number;
  triggerPercentageThreshold?: number;
  triggerConditions?: string;
  actions: EscalationActionFlags;
  escalateToUserId?: string;
  escalateToTeam?: string;
  notificationTemplateId?: string;
  notifyUserIds?: string;
  notifyEmails?: string;
  increasePriority: boolean;
  setPriority?: TicketPriority;
  addAdditionalAgents: boolean;
  additionalAgentCount?: number;
  requiredSkills?: string;
  customScript?: string;
  executionOrder: number;
  isActive: boolean;
  schedule: EscalationSchedule;
  maxExecutions?: number;
  cooldownMinutes?: number;
  createdAt: string;
  updatedAt?: string;
  createdById?: string;
  updatedById?: string;
}

export interface BusinessHoursCalendar {
  id: string;
  name: string;
  description?: string;
  timeZone: string;
  isActive: boolean;
  isDefault: boolean;
  type: CalendarType;
  createdAt: string;
  updatedAt?: string;
  createdById?: string;
  updatedById?: string;
  businessHours: BusinessHours[];
  holidays: Holiday[];
}

export interface BusinessHours {
  id: string;
  calendarId: string;
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  startTime: string; // TimeSpan as string
  endTime: string;   // TimeSpan as string
  isBusinessDay: boolean;
  breakPeriods?: string; // JSON array
  overrideDate?: string;
  notes?: string;
}

export interface Holiday {
  id: string;
  calendarId: string;
  name: string;
  date: string;
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  isFullDay: boolean;
  startTime?: string;
  endTime?: string;
  type: HolidayType;
  applicableRegions?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  createdById?: string;
}

export interface SLAPerformance {
  id: string;
  ticketId: string;
  slaPolicyId: string;
  slaTargetId?: string;
  metricType: SLAMetricType;
  priority: TicketPriority;
  targetMinutes: number;
  actualMinutes?: number;
  businessMinutes?: number;
  pausedMinutes: number;
  slaMet: boolean;
  slaBreached: boolean;
  startTime: string;
  endTime?: string;
  dueDate: string;
  breachNotifiedAt?: string;
  warningNotifications?: string;
  status: SLAStatus;
  isPaused: boolean;
  pausedAt?: string;
  pauseReason?: string;
  createdAt: string;
  updatedAt?: string;
  metadata?: string;
}

export interface SLAEvent {
  id: string;
  ticketId: string;
  slaPolicyId: string;
  eventType: SLAEventType;
  metricType: SLAMetricType;
  priority: TicketPriority;
  targetMinutes: number;
  elapsedMinutes?: number;
  businessElapsedMinutes?: number;
  remainingMinutes?: number;
  isBreach: boolean;
  isPaused: boolean;
  pauseReason?: string;
  triggeredById?: string;
  description?: string;
  timestamp: string;
  metadata?: string;
}

// Enums
export enum SLATier {
  Basic = 0,
  Premium = 1,
  Enterprise = 2,
  VIP = 3,
  Custom = 4
}

export enum EscalationPriorityFlags {
  None = 0,
  Low = 1,
  Medium = 2,
  High = 4,
  Urgent = 8,
  Critical = 16,
  All = 31 // Low | Medium | High | Urgent | Critical
}

export enum EscalationTriggerType {
  TimeBeforeBreach = 0,
  PercentageElapsed = 1,
  OnBreach = 2,
  NoActivity = 3,
  CustomCondition = 4,
  AgentWorkload = 5
}

export enum EscalationActionFlags {
  None = 0,
  Reassign = 1,
  NotifyManager = 2,
  AddWatchers = 4,
  IncreasePriority = 8,
  NotifyCustomer = 16,
  AddToUrgentQueue = 32,
  CustomWorkflow = 64,
  AddResources = 128
}

export enum EscalationSchedule {
  Always = 0,
  BusinessHoursOnly = 1,
  OutsideBusinessHours = 2,
  WeekdaysOnly = 3,
  WeekendsOnly = 4,
  Custom = 5
}

export enum CalendarType {
  Standard = 0,
  TwentyFourSeven = 1,
  Custom = 2,
  Inherited = 3
}

export enum RecurrencePattern {
  Yearly = 0,
  Monthly = 1,
  WeeklyPattern = 2,
  Custom = 3
}

export enum HolidayType {
  National = 0,
  Company = 1,
  Regional = 2,
  Religious = 3,
  Cultural = 4,
  Emergency = 5
}

export enum SLAMetricType {
  FirstResponse = 0,
  Resolution = 1,
  NextResponse = 2,
  Escalation = 3,
  Custom = 4
}

export enum SLAStatus {
  NotStarted = 0,
  InProgress = 1,
  Paused = 2,
  Achieved = 3,
  Breached = 4,
  Cancelled = 5,
  Reset = 6,
  Expired = 7
}

export enum SLAEventType {
  Started = 0,
  Achieved = 1,
  Breached = 2,
  Paused = 3,
  Resumed = 4,
  Updated = 5,
  Warning = 6,
  Escalated = 7,
  Stopped = 8,
  Reset = 9
}

export enum TicketPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Urgent = 3,
  Critical = 4
}

// Request/Response DTOs
export interface CreateSLAPolicyRequest {
  name: string;
  description?: string;
  firstResponseLowPriorityMinutes: number;
  firstResponseMediumPriorityMinutes: number;
  firstResponseHighPriorityMinutes: number;
  firstResponseUrgentPriorityMinutes: number;
  firstResponseCriticalPriorityMinutes: number;
  resolutionLowPriorityMinutes: number;
  resolutionMediumPriorityMinutes: number;
  resolutionHighPriorityMinutes: number;
  resolutionUrgentPriorityMinutes: number;
  resolutionCriticalPriorityMinutes: number;
  businessHoursStart: string;
  businessHoursEnd: string;
  businessDays: string;
  timeZone: string;
  excludeWeekends: boolean;
  excludeHolidays: boolean;
  businessHoursOnly: boolean;
  businessHoursCalendarId?: string;
  tier: SLATier;
  autoPauseOnCustomerWait: boolean;
  enablePredictiveSLA: boolean;
  isActive: boolean;
  isDefault: boolean;
}

export interface UpdateSLAPolicyRequest extends CreateSLAPolicyRequest {}

export interface CreateEscalationRuleRequest {
  name: string;
  description?: string;
  applicablePriorities: EscalationPriorityFlags;
  escalationLevel: number;
  triggerType: EscalationTriggerType;
  triggerMinutesBeforeBreach: number;
  triggerPercentageThreshold?: number;
  triggerConditions?: string;
  actions: EscalationActionFlags;
  escalateToUserId?: string;
  escalateToTeam?: string;
  notificationTemplateId?: string;
  notifyUserIds?: string;
  notifyEmails?: string;
  increasePriority: boolean;
  setPriority?: TicketPriority;
  addAdditionalAgents: boolean;
  additionalAgentCount?: number;
  requiredSkills?: string;
  customScript?: string;
  executionOrder: number;
  isActive: boolean;
  schedule: EscalationSchedule;
  maxExecutions?: number;
  cooldownMinutes?: number;
}

export interface UpdateEscalationRuleRequest extends CreateEscalationRuleRequest {}

export interface CreateBusinessHoursCalendarRequest {
  name: string;
  description?: string;
  timeZone: string;
  isActive: boolean;
  isDefault: boolean;
  type: CalendarType;
}

export interface SLADashboardData {
  totalActivePolicies: number;
  totalActiveTickets: number;
  overallComplianceRate: number | null;
  ticketsAtRisk: number;
  ticketsBreached: number;
  breachAlerts: SLABreachAlert[];
  complianceByPriority: { [key: string]: number };
  /**
   * True when one or more sources failed and substituted values are shown.
   * The dashboard must label itself rather than pass these off as live figures.
   */
  isPartialData?: boolean;
  recentActivity: SLAEvent[];
}

export interface SLABreachAlert {
  ticketId: string;
  ticketNumber: string;
  title: string;
  priority: TicketPriority;
  metricType: SLAMetricType;
  timeRemaining: number; // minutes (negative if breached)
  dueDate: string;
  assignedAgentName?: string;
  customerName?: string;
  isBreached: boolean;
  warningLevel: 'warning' | 'critical' | 'breached';
}

export interface SLAValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface SLAPerformanceReport {
  startDate: string;
  endDate: string;
  groupBy: SLAReportGrouping;
  totalTickets: number;
  overallComplianceRate: number;
  metricSummaries: { [key: string]: SLAMetricSummary };
  groups: SLAPerformanceGroup[];
}

export interface SLAMetricSummary {
  metricType: SLAMetricType;
  totalTickets: number;
  achievedCount: number;
  breachedCount: number;
  complianceRate: number;
  averageResponseMinutes: number;
  medianResponseMinutes: number;
}

export interface SLAPerformanceGroup {
  groupName: string;
  groupValue: string;
  totalTickets: number;
  complianceRate: number;
  metrics: { [key: string]: SLAMetricSummary };
}

export enum SLAReportGrouping {
  None = 0,
  Priority = 1,
  Agent = 2,
  Team = 3,
  Category = 4,
  Company = 5,
  Channel = 6,
  Day = 7,
  Week = 8,
  Month = 9
}

// UI Helper Types
export interface SLAPolicyFormData {
  name: string;
  description: string;
  tier: SLATier;
  isActive: boolean;
  isDefault: boolean;
  businessHoursOnly: boolean;
  businessHoursStart: { hours: number; minutes: number };
  businessHoursEnd: { hours: number; minutes: number };
  businessDays: string;
  timeZone: string;
  excludeWeekends: boolean;
  excludeHolidays: boolean;
  autoPauseOnCustomerWait: boolean;
  enablePredictiveSLA: boolean;
  businessHoursCalendarId: string;
  firstResponseLowPriorityMinutes: number;
  firstResponseMediumPriorityMinutes: number;
  firstResponseHighPriorityMinutes: number;
  firstResponseUrgentPriorityMinutes: number;
  firstResponseCriticalPriorityMinutes: number;
  resolutionLowPriorityMinutes: number;
  resolutionMediumPriorityMinutes: number;
  resolutionHighPriorityMinutes: number;
  resolutionUrgentPriorityMinutes: number;
  resolutionCriticalPriorityMinutes: number;
}

export interface SLAFilterOptions {
  search: string;
  tier?: SLATier;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface EscalationRuleFormData {
  name: string;
  description: string;
  escalationLevel: number;
  applicablePriorities: EscalationPriorityFlags;
  triggerType: EscalationTriggerType;
  triggerMinutesBeforeBreach: number;
  triggerPercentageThreshold?: number;
  actions: EscalationActionFlags;
  escalateToUserId?: string;
  escalateToTeam?: string;
  notifyEmails?: string;
  increasePriority: boolean;
  setPriority?: TicketPriority;
  addAdditionalAgents: boolean;
  additionalAgentCount?: number;
  schedule: EscalationSchedule;
  maxExecutions?: number;
  cooldownMinutes?: number;
  isActive: boolean;
}

// Utility Types
export interface TimeSpanValue {
  hours: number;
  minutes: number;
}

export interface BusinessDay {
  day: string;
  enabled: boolean;
}